import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import styles from "./SuggestedQuestionInput.module.css";

function resizeInput(input: HTMLTextAreaElement) {
  input.style.overflowY = "hidden";
  input.style.height = "0px";
  const height = input.scrollHeight;
  input.style.height = `${Math.min(height, 144)}px`;
  input.style.overflowY = height > 144 ? "auto" : "hidden";
}

function resizeAcceptanceArea(input: HTMLTextAreaElement) {
  const target = input.parentElement?.querySelector<HTMLButtonElement>("[data-accept-suggestion]");
  if (!target) return;
  const right = input.getBoundingClientRect().right - 10;
  target.style.width = `${Math.max(44, right - target.getBoundingClientRect().left)}px`;
}

/** Optional suggested text is never part of the learner's draft until accepted. */
export function SuggestedQuestionInput({
  value,
  onChange,
  onSend,
  disabled,
  placeholder,
  inputClassName,
  suggestions,
}: {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled: boolean;
  placeholder: string;
  inputClassName: string;
  suggestions: readonly string[];
}) {
  const hintId = useId();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const suggestionEndRef = useRef<HTMLSpanElement>(null);
  const acceptedRef = useRef(false);
  const [handled, setHandled] = useState<ReadonlySet<string>>(() => new Set());
  const suggestion = suggestions
    .map((text) => text.trim())
    .find((text) => text && !handled.has(text));
  const visibleSuggestion = !disabled && !value.length ? suggestion : undefined;

  useLayoutEffect(() => {
    if (inputRef.current) {
      if (visibleSuggestion) {
        inputRef.current.style.height = "";
        inputRef.current.style.overflowY = "hidden";
        resizeAcceptanceArea(inputRef.current);
      } else resizeInput(inputRef.current);
    }
    if (!acceptedRef.current) return;
    acceptedRef.current = false;
    inputRef.current?.focus();
    inputRef.current?.setSelectionRange(value.length, value.length);
  }, [value, visibleSuggestion]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input || typeof ResizeObserver === "undefined") return;
    let width = input.getBoundingClientRect().width;
    const observer = new ResizeObserver(() => {
      const nextWidth = input.getBoundingClientRect().width;
      if (input.parentElement?.hasAttribute("data-has-suggestion")) {
        resizeAcceptanceArea(input);
      } else if (nextWidth !== width) {
        width = nextWidth;
        resizeInput(input);
      }
    });
    observer.observe(input.parentElement ?? input);
    return () => observer.disconnect();
  }, []);

  const accept = useCallback(() => {
    if (!visibleSuggestion) return;
    acceptedRef.current = true;
    setHandled((previous) => new Set([...previous, visibleSuggestion]));
    onChange(visibleSuggestion);
  }, [visibleSuggestion, onChange]);

  useEffect(() => {
    if (!visibleSuggestion) return;
    const handleWorkspaceTab = (event: KeyboardEvent) => {
      const input = inputRef.current;
      if (
        event.defaultPrevented ||
        event.key !== "Tab" ||
        event.shiftKey ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.isComposing ||
        event.keyCode === 229 ||
        !input ||
        !input.getClientRects().length ||
        input.closest("[inert], [hidden]") ||
        (document.activeElement !== document.body &&
          document.activeElement !== document.documentElement)
      )
        return;
      event.preventDefault();
      accept();
    };
    document.addEventListener("keydown", handleWorkspaceTab);
    return () => document.removeEventListener("keydown", handleWorkspaceTab);
  }, [visibleSuggestion, accept]);

  const dismiss = () => {
    setHandled((previous) => new Set([...previous, ...suggestions.map((text) => text.trim())]));
  };

  return (
    <div
      className={styles.field}
      data-has-suggestion={visibleSuggestion ? true : undefined}
      onPointerDown={(event) => {
        if (!visibleSuggestion || (event.target as Element).closest("[data-accept-suggestion]"))
          return;
        event.preventDefault();
        inputRef.current?.focus();
      }}
      onClick={(event) => {
        if (!visibleSuggestion || (event.target as Element).closest("[data-accept-suggestion]"))
          return;
        const end = suggestionEndRef.current?.getBoundingClientRect();
        if (!end) return;
        const field = event.currentTarget.getBoundingClientRect();
        // The textarea also receives clicks in the blank padding around the
        // trailing button. Measure the current sentence when the click arrives.
        if (
          event.clientX >= end.left &&
          event.clientX <= field.right &&
          event.clientY >= Math.max(field.top, end.top - 12) &&
          event.clientY <= field.bottom
        )
          accept();
      }}
    >
      <textarea
        ref={inputRef}
        data-transcript-input
        aria-label="Message to tutor"
        aria-describedby={visibleSuggestion ? hintId : undefined}
        className={`${inputClassName} ${styles.input}`}
        rows={1}
        value={value}
        placeholder={visibleSuggestion ? "" : placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return;
          if (event.altKey || event.ctrlKey || event.metaKey) return;
          if (event.key === "Tab" && !event.shiftKey && visibleSuggestion) {
            event.preventDefault();
            accept();
          } else if (event.key === "Escape" && visibleSuggestion) {
            event.preventDefault();
            event.stopPropagation();
            dismiss();
          } else if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            onSend();
          }
        }}
      />
      {visibleSuggestion ? (
        <>
          <div className={styles.suggestion}>
            <span aria-hidden="true">{visibleSuggestion}</span>
            <span ref={suggestionEndRef} className={styles.acceptAnchor}>
              <button
                data-accept-suggestion
                type="button"
                tabIndex={-1}
                className={styles.accept}
                aria-label="Use suggested question"
                onClick={accept}
              />
            </span>
          </div>
          <span id={hintId} className="sr-only">
            Suggested question: {visibleSuggestion} Press Tab or click just after the question to
            use it. Clicking elsewhere focuses an empty draft; type your own message or press Escape
            to dismiss the suggestion. Accepting a suggestion does not send it.
          </span>
        </>
      ) : null}
    </div>
  );
}
